/**
 * AVS-4302: per-tab VOIP master claim memory + deterministic conflict resolution.
 *
 * Two same-dealer tabs can claim a vacant `voipMasters[myPseudonym]` slot near-simultaneously
 * (each tab's publish gate fires when the OTHER's connection arrives), and the claims cross in
 * transit (the session sync shallow-replaces top-level keys). Each tab then holds the OTHER's
 * claim: both step down, both render the standby "Takeover" tile, and nobody publishes — the
 * customer loses the dealer stream entirely.
 *
 * Resolution: every claim carries a generation (`gen`), and each tab privately remembers the
 * gen of its own latest claim. On observing a slot held by another tab:
 *   - higher gen wins (a takeover claims gen+1, so it can never be stolen back);
 *   - equal gen is the crossed-claim race — the LOWER tabId wins deterministically, so both
 *     tabs independently reach the same verdict and converge in one broadcast round.
 * The winner re-asserts its claim; the loser concedes (steps down to receive-only standby).
 */

export type TVoipMasterSlot = { tabId: string; gen?: number };

export type TVoipClaimAction = 'none' | 'reassert' | 'stepDown';

/** Upper bound for gen — mirrors the schema bound so `gen + 1` stays representable. */
export const VOIP_CLAIM_GEN_MAX = 1_000_000_000;

/** Re-asserts allowed before conceding (brake against pathological broadcast flapping). */
const MAX_REASSERTS = 5;

/** A slot stably ours for this long refills the re-assert budget. */
const REASSERT_BUDGET_RESET_MS = 10_000;

let myClaimGen: number | null = null;
let reassertBudget = MAX_REASSERTS;
let lastReassertAt = 0;

/** The gen this tab's next claim should carry, clamped to the schema bound. */
export const nextVoipClaimGen = (slot: TVoipMasterSlot | undefined): number =>
  Math.min((slot?.gen ?? 0) + 1, VOIP_CLAIM_GEN_MAX);

export interface IVoipMastersMergeResult {
  merged: Record<string, TVoipMasterSlot>;
  /** True when the local map won over a DIFFERING own-slot value — the server may still hold the loser. */
  ownSlotDefended: boolean;
}

/**
 * AVS-4330: gen-aware merge of an incoming `voipMasters` map into the local one. The key is
 * self-reconciling (higher gen wins; equal gen → lower tabId, matching resolveVoipClaimAction),
 * so the remote-apply layer merges it instead of letting the version gate / dirty-skip
 * arbitrate — either gate could drop or revert a takeover broadcast. Foreign local-only entries
 * defer to the incoming map (departures propagate); the own registered claim is preserved (it
 * has no legitimate remote-deletion path). A defended own slot needs a corrective re-push —
 * the server is last-writer-wins and may still hold the losing map.
 */
export function mergeVoipMasters(
  local: Record<string, TVoipMasterSlot> | undefined,
  incoming: Record<string, TVoipMasterSlot>,
  ownPseudonym: string | undefined
): IVoipMastersMergeResult {
  const merged: Record<string, TVoipMasterSlot> = { ...incoming };
  let ownSlotDefended = false;

  Object.entries(local ?? {}).forEach(([pseudonym, localSlot]) => {
    const incomingSlot = incoming[pseudonym];
    const isOwn = ownPseudonym !== undefined && pseudonym === ownPseudonym;

    if (!incomingSlot) {
      if (isOwn && myClaimGen !== null) {
        merged[pseudonym] = localSlot;
        ownSlotDefended = true;
      }
      return;
    }

    const localGen = localSlot.gen ?? 0;
    const incomingGen = incomingSlot.gen ?? 0;
    const localWins = localGen > incomingGen || (localGen === incomingGen && localSlot.tabId < incomingSlot.tabId);

    if (localWins) {
      merged[pseudonym] = localSlot;
      if (isOwn && (localSlot.tabId !== incomingSlot.tabId || localGen !== incomingGen)) {
        ownSlotDefended = true;
      }
    }
  });

  return { merged, ownSlotDefended };
}

/**
 * Spend one unit of the shared reassert budget for a corrective re-push after a defended
 * merge. Shares the flap brake with resolveVoipClaimAction so repairs + reasserts together
 * cannot PUT-storm; the budget refills after a quiet window.
 */
export function consumeReassertBudgetForRepair(): boolean {
  if (Date.now() - lastReassertAt > REASSERT_BUDGET_RESET_MS) {
    reassertBudget = MAX_REASSERTS;
  }
  if (reassertBudget <= 0) return false;
  reassertBudget -= 1;
  lastReassertAt = Date.now();
  return true;
}

/**
 * Record that THIS tab just wrote a claim with `gen`. Called synchronously at every claim
 * site (gate claim, share(), takeover) — BEFORE any broadcast can cross — so the resolver
 * can distinguish "I contended and lost" from "I never claimed" without depending on a
 * React render having committed (a crossed claim can land between claim and commit).
 */
export function registerVoipClaim(gen: number): void {
  myClaimGen = Math.min(gen, VOIP_CLAIM_GEN_MAX);
}

export function getVoipClaimGen(): number | null {
  return myClaimGen;
}

/** Forget the claim — this tab conceded, or the session changed. */
export function clearVoipClaim(): void {
  myClaimGen = null;
  reassertBudget = MAX_REASSERTS;
  lastReassertAt = 0;
}

/**
 * Decide how this tab reacts to the observed slot state. Pure verdicts; the caller performs
 * the side effects (re-assert write / step-down rebuild).
 *
 *   - vacant slot → 'none' (C1: transient clobber self-heals via the gate re-claim; the claim
 *     memory SURVIVES so a later genuine takeover still steps this tab down);
 *   - slot is mine → adopt its gen (covers reload: sessionStorage keeps the tabId) → 'none';
 *   - slot is another tab's and I never claimed → 'none' (plain standby);
 *   - slot is another tab's and I claimed:
 *       holder gen higher → 'stepDown' (their claim is newer — a takeover);
 *       holder gen lower  → 'reassert' (my claim is newer — their write is stale);
 *       equal gen         → crossed-claim race: lower tabId wins.
 * A 'reassert' consumes budget; when exhausted this tab concedes instead (a capped tab must
 * never keep publishing against a slot it does not hold). Budget refills once the slot has
 * been stably ours for a while.
 */
export function resolveVoipClaimAction(slot: TVoipMasterSlot | undefined, myTabId: string): TVoipClaimAction {
  if (!slot?.tabId) return 'none';

  if (slot.tabId === myTabId) {
    myClaimGen = slot.gen ?? myClaimGen ?? 0;
    if (Date.now() - lastReassertAt > REASSERT_BUDGET_RESET_MS) {
      reassertBudget = MAX_REASSERTS;
    }
    return 'none';
  }

  if (myClaimGen === null) return 'none';

  const holderGen = slot.gen ?? 0;
  const iWin = holderGen < myClaimGen || (holderGen === myClaimGen && myTabId < slot.tabId);

  if (iWin && reassertBudget > 0) {
    reassertBudget -= 1;
    lastReassertAt = Date.now();
    return 'reassert';
  }

  clearVoipClaim();
  return 'stepDown';
}
